import { WidgetType } from 'widgets/WidgetType';
import {
    WidgetCustomStreamManager,
    nullWidgetCustomStreamManager,
} from './WidgetCustomStreamManager';
import { WidgetId } from 'widgets/WidgetId';
import {
    ActionsDispatcher,
    ActionsImmediateDispatcher,
} from 'store/ActionsDispatcher';
import { IndigoStore } from 'store/store';
import { Logger } from '@instinet-analytics/core-lib';

/*
 * Implemented by stream managers that manage a stream for a set of symbols
 */
export interface WidgetCustomStreamManagerSupportingRegisteredSymbols<TConfig>
    extends WidgetCustomStreamManager {
    registerSymbolStreams: (
        widgetKey: string,
        widgetType: WidgetType,
        symbols: string[],
        config: TConfig,
        quantities?: number[]
    ) => void;
}

export const nullWidgetCustomStreamManagerSupportingRegisteredSymbols: WidgetCustomStreamManagerSupportingRegisteredSymbols<unknown> =
    {
        registerSymbolStreams: (_k, _t, _s, _c) => {
            return;
        },

        ...nullWidgetCustomStreamManager,
    };

export function isWidgetCustomStreamManagerSupportingRegisteredSymbols(
    manager: WidgetCustomStreamManager
): manager is WidgetCustomStreamManagerSupportingRegisteredSymbols<unknown> {
    return 'registerSymbolStreams' in manager;
}

export abstract class WidgetCustomStreamManagerSupportingRegisteredSymbolsBase
    implements WidgetCustomStreamManagerSupportingRegisteredSymbols<unknown>
{
    protected readonly store: IndigoStore;
    protected widgetIds: WidgetId[] = [];
    protected registeredSymbolStreams: {
        [key: string]: {
            widgetType: WidgetType;
            symbols: string[];
            config: unknown;
            quantities?: number[];
        };
    } = {};
    protected readonly logger: Logger;

    protected constructor(store: IndigoStore, logger: Logger) {
        this.store = store;
        this.logger = logger;
    }

    public abstract resetState(): void;

    public register(widgetId: WidgetId): { handled: boolean } {
        const existing = this.findWidgetId(widgetId);

        if (!existing) {
            this.widgetIds.push(widgetId);
        }
        this.checkStream(
            `: registered ${widgetId.widgetType}=${widgetId.widgetKey}`,
            new ActionsImmediateDispatcher(this.store)
        );
        return { handled: true };
    }

    public dispose(widgetId: WidgetId): { handled: boolean } {
        const existing = this.findWidgetId(widgetId);
        if (existing) {
            this.widgetIds.splice(this.widgetIds.indexOf(existing), 1);
        }

        if (this.registeredSymbolStreams[widgetId.widgetKey]) {
            delete this.registeredSymbolStreams[widgetId.widgetKey];
        }

        this.checkStream(
            `: disposed ${widgetId.widgetType}=${widgetId.widgetKey}`,
            new ActionsImmediateDispatcher(this.store)
        );
        return { handled: true };
    }

    private findWidgetId(widgetId: WidgetId) {
        return this.widgetIds.find(
            (w) =>
                w.widgetKey === widgetId.widgetKey &&
                w.widgetType === widgetId.widgetType
        );
    }

    public registerSymbolStreams<TConfig>(
        widgetKey: string,
        widgetType: WidgetType,
        symbols: string[],
        config: TConfig,
        quantities?: number[]
    ): void {
        this.logger.debug(
            'Registered symbol streams for ' + widgetKey,
            symbols
        );

        this.registeredSymbolStreams[widgetKey] = {
            widgetType,
            symbols,
            config,
            quantities,
        };
        this.checkStream(
            'Symbol Stream Registration',
            new ActionsImmediateDispatcher(this.store)
        );
    }

    public abstract checkStream(
        reason: string,
        dispatcher: ActionsDispatcher
    ): void;
    public abstract getInfo(): Record<string, unknown>;

    public onAppSettingsChanged(dispatcher: ActionsDispatcher): void {
        this.resetState();
        this.checkStream('AppSettings changed', dispatcher);
    }
    public onPortfolioStreamingOrderIdsChanged(
        _dispatcher: ActionsDispatcher
    ): void {
        return;
    }
    public onWidgetComponentStateChanged(
        _widgetId: WidgetId,
        _dispatcher: ActionsDispatcher
    ) {
        return;
    }
    public onFilteredPortfolioChangedAsync(
        _dispatcher: ActionsDispatcher
    ): void {
        return;
    }

    public onPortfolioSelectionChanged(dispatcher: ActionsDispatcher): void {
        this.checkStream('selection changed', dispatcher);
    }

    public onWidgetConfigChanged(
        widgetId: WidgetId,
        dispatcher: ActionsDispatcher
    ) {
        this.checkStream(
            `: config changed for ${widgetId.widgetType}=${widgetId.widgetKey}`,
            dispatcher
        );
        return;
    }
}
